import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Button,
  TextInput,
  NumberInput,
  Select,
  MultiSelect,
  Switch,
  FileInput,
  Group,
  Image,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  addCategory,
  updateCategory,
  uploadImage,
  getCategorysList,
} from "../../../../services/category";
import { getAllCategoryRules } from "../../../../services/category-rules";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconX, IconUpload } from "@tabler/icons-react";

const FormProductCategory = ({
  dataDetail,
  onCloseModal,
  reloadList,
  currentParentId,
}) => {
  const [loading, setLoading] = useState(false);
  const [rulesOptions, setRulesOptions] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [imageFile, setImageFile] = useState(null);

  const initialTopCategory = dataDetail
    ? !dataDetail.attributes.parent_id
    : !currentParentId;

  const [isTopCategory, setIsTopCategory] = useState(initialTopCategory);

  const form = useForm({
    initialValues: {
      name: dataDetail ? dataDetail.attributes.name : "",
      priority: dataDetail ? dataDetail.attributes.priority : 0,
      category_type: dataDetail ? dataDetail.attributes.category_type : 1,
      parent_id: dataDetail
        ? dataDetail.attributes.parent_id
        : currentParentId || null,
      rule: (dataDetail?.relationships?.rules || dataDetail?.relationships?.category_rules)?.length
        ? (dataDetail.relationships.rules || dataDetail.relationships.category_rules).map((r) => String(r.id))
        : [],
    },
    validate: {
      name: (value) =>
        value.trim().length === 0 ? "Name is required" : null,
      parent_id: (value) =>
        !isTopCategory && !value ? "Parent Category is required" : null,
    },
  });

  useEffect(() => {
    fetchRules();
    if (!isTopCategory) {
      fetchParents();
    }
  }, [isTopCategory]);

  const fetchRules = async () => {
    try {
      const res = await getAllCategoryRules({ "page[limit]": 1000 });
      if (res.data) {
        setRulesOptions(
          res.data.map((r) => ({
            value: String(r.id),
            label: r.attributes?.description || `Rule ${r.id}`,
          }))
        );
      }
    } catch (e) {
      console.error("Error fetching rules", e);
    }
  };

  const fetchParents = async () => {
    try {
      const res = await getCategorysList({ "page[limit]": 500 , "filter[level][in]": "1,2"});
      // Depending on API response structure
      const list = res?.data?.data || res?.data || [];
      setParentOptions(
        list.map((c) => ({
          value: String(c.id),
          label: c.attributes?.name || `Category ${c.id}`,
        }))
      );
    } catch (e) {
      console.error("Error fetching parent options", e);
    }
  };

  const handleSaveImage = async (categoryId) => {
    if (!imageFile) return;
    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      await uploadImage(categoryId, formData);
    } catch (e) {
      console.error("Failed uploading image:", e);
      notifications.show({
        title: "Image Upload Failed",
        message: "Failed to upload the category image.",
        color: "yellow",
      });
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    // level depends on if it has a parent. If no parent (Top category), level=1.
    // If has parent, usually backend calculates level, or we just pass level=2 for now.
    // Assuming level is derived from parent or just simplified. Let's pass level 1 or 2 based on isTopCategory
    const payload = {
      name: values.name,
      priority: Number(values.priority),
      category_type: Number(values.category_type),
      parent_id: isTopCategory ? null : Number(values.parent_id),
      level: isTopCategory ? 1 : 2, // Backend mostly handles level ideally
      rule: values.rule.map((r) => Number(r)),
    };

    try {
      let res;
      if (dataDetail) {
        res = await updateCategory(dataDetail.id, payload);
        if (imageFile) {
          await handleSaveImage(dataDetail.id);
        }
      } else {
        res = await addCategory(payload);
        const newId = res.data?.id || res.id;
        if (newId && imageFile) {
          await handleSaveImage(newId);
        }
      }

      notifications.show({
        title: "Success",
        message: `Category has been ${
          dataDetail ? "updated" : "added"
        } successfully`,
        color: "teal",
        icon: <IconCheck />,
      });
      onCloseModal();
      reloadList();
    } catch (error) {
      console.error("Submit error:", error);
      notifications.show({
        title: "Error",
        message: error?.response?.data?.message || "Something went wrong",
        color: "red",
        icon: <IconX />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Box mb="md">
          <Switch
            label="Top Category"
            checked={isTopCategory}
            onChange={(event) => {
              setIsTopCategory(event.currentTarget.checked);
              if (event.currentTarget.checked) {
                form.setFieldValue("parent_id", null);
              }
            }}
            mb="md"
          />

          {!isTopCategory && (
            <Select
              label="Parent Category"
              placeholder="Select parent"
              data={parentOptions}
              searchable
              {...form.getInputProps("parent_id")}
              mb="md"
            />
          )}

          <TextInput
            label="Name"
            placeholder="Category Name"
            withAsterisk
            {...form.getInputProps("name")}
            mb="md"
          />

          <NumberInput
            label="Priority"
            placeholder="0"
            {...form.getInputProps("priority")}
            mb="md"
          />

          <MultiSelect
            label="Category Rules"
            placeholder="Select rules"
            data={rulesOptions}
            searchable
            clearable
            {...form.getInputProps("rule")}
            mb="md"
          />

          <FileInput
            label="Image"
            placeholder="Upload Image"
            accept="image/*"
            icon={<IconUpload size={14} />}
            onChange={setImageFile}
            mb="md"
          />

          {imageFile && (
            <Box mb="sm">
              <Image
                src={URL.createObjectURL(imageFile)}
                w={100}
                h={100}
                radius="md"
                fit="cover"
              />
            </Box>
          )}

          {!imageFile && dataDetail?.attributes?.image && (
            <Box mb="sm">
              <Image
                src={dataDetail.attributes.image}
                w={100}
                h={100}
                radius="md"
                fit="cover"
              />
            </Box>
          )}
        </Box>

        <Flex justify="flex-end" gap="sm">
          <Button variant="outline" color="gray" onClick={onCloseModal}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Save
          </Button>
        </Flex>
      </form>
    </Box>
  );
};

export default FormProductCategory;
